import { cn } from "@/lib/utils";

interface FilterToggleOption {
  value: string;
  label: string;
}

interface FilterToggleGroupProps {
  options: FilterToggleOption[];
  /** Selected values. An empty selection means "all" — no filter applied. */
  value: string[];
  onChange: (value: string[]) => void;
  /** Label for the leading "all / no filter" chip. */
  allLabel?: string;
}

const CHIP_CLASS =
  "inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border px-2 py-1 text-xs font-medium transition-colors";
const ACTIVE_CLASS = "border-primary bg-primary text-primary-foreground";
const INACTIVE_CLASS = "border-input bg-card text-foreground hover:bg-muted/50";

/**
 * Multi-select pill button group used as a quick filter (status, capacity, …).
 * A leading "All" chip is active whenever nothing else is selected (the
 * default, no-filter state); clicking it clears any specific selection.
 * Selecting any option deselects "All" automatically, and deselecting the last
 * option re-activates it. Active chip fills navy, the rest are bordered.
 */
export function FilterToggleGroup({
  options,
  value,
  onChange,
  allLabel = "All",
}: FilterToggleGroupProps) {
  function toggle(v: string) {
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  }

  const allActive = value.length === 0;

  return (
    <>
      <button
        key="__all__"
        type="button"
        aria-pressed={allActive}
        onClick={() => onChange([])}
        className={cn(CHIP_CLASS, allActive ? ACTIVE_CLASS : INACTIVE_CLASS)}
      >
        {allLabel}
      </button>
      {options.map((opt) => {
        const active = value.includes(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            aria-pressed={active}
            onClick={() => toggle(opt.value)}
            className={cn(CHIP_CLASS, active ? ACTIVE_CLASS : INACTIVE_CLASS)}
          >
            {opt.label}
          </button>
        );
      })}
    </>
  );
}
